import { type JSX, useState } from "react";

import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import { CountSpinner } from "common/components/ui/CountSpinner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "common/components/ui/Dialog";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import usePromotions from "common/datahooks/usePromotions";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";

import LicenseAccounts from "./LicenseAccounts";

interface ManageAgencySeatsDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export default function ManageAgencySeatsDialog({
  isOpen,
  setIsOpen,
}: ManageAgencySeatsDialogProps) {
  const { agencyPlan, isLoadingPromotions } = usePromotions();
  const { id: workspaceId, agency_license_count } =
    useSelectedWorkspaceContext();
  const { setLifetimeLicenseCount, isSettingLifetimeLicenseCount } =
    useLicenseMutations();
  const { agencyLicensedAccounts } = useLicensedAccounts(workspaceId);

  const [seatCount, setSeatCount] = useState(agency_license_count);

  async function onSubmit() {
    if (seatCount !== agency_license_count) {
      await setLifetimeLicenseCount({
        workspaceId,
        count: seatCount,
        source: "agency",
        accountsToCancel: [],
      });
    }
    setIsOpen(false);
  }

  let alertComponent: JSX.Element;

  if (seatCount < agencyLicensedAccounts.length) {
    alertComponent = (
      <Alert variant="warning" className="mt-4">
        Cannot remove seats assigned to accounts. Unassign seats to continue
      </Alert>
    );
  } else if (seatCount < agency_license_count) {
    alertComponent = (
      <Alert variant="default" className="mt-4">
        Removing Agency seats will return them to the Agency Plan pool and
        reduce the total number of available seats for all users in this
        workspace
      </Alert>
    );
  }

  const unassignedLicenseCount = agencyPlan
    ? agencyPlan.unassigned_license_count
    : 0;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogTitle className="mb-10">Manage workspace seats</DialogTitle>
        <CountSpinner
          variant="lg"
          value={seatCount}
          setValue={setSeatCount}
          maxValue={agency_license_count + unassignedLicenseCount}
        />
        <div className="flex gap-x-2 mt-4">
          <div className="flex flex-col p-3 border flex-1 gap-y-1 rounded-2xl border-black-200">
            <span className="text-black-500">Assigned</span>
            <div className="flex items-center gap-x-2.5">
              <span className="text-2xl-bold">
                {agencyLicensedAccounts.length}
              </span>
              <LicenseAccounts accounts={agencyLicensedAccounts} />
            </div>
          </div>
          <div className="flex flex-col p-3 border flex-1 gap-y-1 rounded-2xl border-black-200">
            <span className="text-black-500">Available in pool</span>
            {isLoadingPromotions ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <span className="text-2xl-bold text-purple-400">
                {unassignedLicenseCount}
              </span>
            )}
          </div>
        </div>
        {alertComponent}
        <Button
          className="ml-auto mt-10"
          isLoading={isSettingLifetimeLicenseCount}
          disabled={seatCount < agencyLicensedAccounts.length}
          onClick={onSubmit}
        >
          Update seats
        </Button>
      </DialogContent>
    </Dialog>
  );
}
